import React, { useState } from 'react';
import {
    Button,
    Card,
    Empty,
    Form,
    InputNumber,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import {
    useCollateralSearch,
    useLoanCollateralLinkMutations,
    useLoanCollateralLinks,
} from '../hooks/useCollaterals';
import {
    Collateral,
    LinkCollateralRequest,
    LoanCollateralLink,
    LoanKey,
} from '../types/collateral.types';

const { Text } = Typography;

interface LoanCollateralLinksPanelProps {
    loanKey: LoanKey;
}

interface LinkFormValues {
    collateralId: number;
    pledgedValue?: number;
    notes?: string;
}

/**
 * Reusable panel that shows every collateral linked to a loan and lets users
 * pledge additional collaterals or release existing ones. Embed this on a loan
 * detail page or wherever a loan context exists.
 */
const LoanCollateralLinksPanel: React.FC<LoanCollateralLinksPanelProps> = ({
    loanKey,
}) => {
    const [form] = Form.useForm<LinkFormValues>();
    const [modalOpen, setModalOpen] = useState(false);

    const { data: links = [], isLoading } = useLoanCollateralLinks(loanKey);
    const { link, unlink } = useLoanCollateralLinkMutations(loanKey);

    // Only show AVAILABLE collaterals in the branch as pledge candidates.
    const { data: candidateData } = useCollateralSearch({
        branch: loanKey.branch,
        status: 'AVAILABLE',
        size: 100,
    });
    const candidates: Collateral[] = candidateData?.content ?? [];

    const handleSubmit = (values: LinkFormValues) => {
        const body: LinkCollateralRequest = {
            collateralId: values.collateralId,
            pledgedValue: values.pledgedValue,
            notes: values.notes,
        };
        link.mutate(body, {
            onSuccess: () => {
                form.resetFields();
                setModalOpen(false);
            },
        });
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'collateralCode',
            key: 'collateralCode',
            render: (v: string) => <Text strong>{v}</Text>,
        },
        { title: 'Type', dataIndex: 'collateralType', key: 'collateralType' },
        {
            title: 'Estimated Value',
            dataIndex: 'collateralEstimatedValue',
            key: 'collateralEstimatedValue',
            render: (v?: number) => (v != null ? `TZS ${v.toLocaleString()}` : '—'),
        },
        {
            title: 'Pledged Value',
            dataIndex: 'pledgedValue',
            key: 'pledgedValue',
            render: (v?: number) => (v != null ? `TZS ${v.toLocaleString()}` : '—'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status?: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>{status}</Tag>
            ),
        },
        {
            title: '',
            key: 'actions',
            render: (_: unknown, row: LoanCollateralLink) =>
                row.status === 'ACTIVE' && row.id ? (
                    <Popconfirm
                        title="Release this collateral from the loan?"
                        okText="Release"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => unlink.mutate(row.id!)}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DisconnectOutlined />}
                            loading={unlink.isPending}
                        >
                            Release
                        </Button>
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <Card
            size="small"
            title={
                <Space>
                    <Text strong>Pledged Collaterals</Text>
                    <Tag>{links.length}</Tag>
                </Space>
            }
            extra={
                <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => setModalOpen(true)}
                    disabled={candidates.length === 0}
                >
                    Pledge Collateral
                </Button>
            }
        >
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                </div>
            ) : links.length === 0 ? (
                <Empty description="No collaterals pledged to this loan yet" />
            ) : (
                <Table<LoanCollateralLink>
                    size="small"
                    rowKey={(row) => row.id ?? 0}
                    dataSource={links}
                    columns={columns}
                    pagination={false}
                />
            )}

            <Modal
                title="Pledge Collateral"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Pledge"
                confirmLoading={link.isPending}
                destroyOnClose
            >
                <Form<LinkFormValues>
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="collateralId"
                        label="Collateral"
                        rules={[{ required: true, message: 'Select a collateral' }]}
                    >
                        <Select
                            placeholder="Select an available collateral"
                            showSearch
                            optionFilterProp="label"
                            options={candidates.map((c) => ({
                                value: c.id,
                                label: `${c.code} — ${c.collateralType}${
                                    c.estimatedValue
                                        ? ` (TZS ${c.estimatedValue.toLocaleString()})`
                                        : ''
                                }`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="pledgedValue" label="Pledged Value (TZS)">
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            formatter={(v) =>
                                `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={(v) =>
                                (Number(`${v}`.replace(/,/g, '')) || 0) as unknown as 0
                            }
                        />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} placeholder="Optional notes" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default LoanCollateralLinksPanel;
